import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import SettingsModal from '../components/SettingsModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, Save, Trash2, Clock, XCircle,
  DollarSign, Loader2, Phone, Calendar
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDERED_VIA_OPTIONS = [
  'Viber', 'WhatsApp', 'Instagram', 'Facebook', 'Messenger',
  'Message', 'Call', 'TikTok', 'Website', 'Telegram', 'Other'
];

const OrderDetail = () => {
  const { outletId, orderId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [order, setOrder] = useState(null);
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showCustomVia, setShowCustomVia] = useState(false);
  
  // Dialog states
  const [notDeliveredDialog, setNotDeliveredDialog] = useState(false);
  const [verifyPaymentDialog, setVerifyPaymentDialog] = useState(false);
  const [paidDialog, setPaidDialog] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState('');
  
  const [formData, setFormData] = useState({
    location: '', contact_no: '', item_details: '', quantity: 1,
    payment_method: '', payment_status: '', status: '',
    delivery_status: '', delivery_reason: '', ordered_via: '', customer_notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const [orderRes, outletRes] = await Promise.all([
        axios.get(`${API}/orders/${orderId}`, { headers }),
        axios.get(`${API}/outlets/${outletId}`, { headers })
      ]);
      setOrder(orderRes.data);
      setOutlet(outletRes.data);
      const d = orderRes.data;
      setFormData({
        location: d.location || '', contact_no: d.contact_no || '',
        item_details: d.item_details || '', quantity: d.quantity || 1,
        payment_method: d.payment_method || '', payment_status: d.payment_status || '',
        status: d.status || 'new', delivery_status: d.delivery_status || '',
        delivery_reason: d.delivery_reason || '', ordered_via: d.ordered_via || '',
        customer_notes: d.customer_notes || ''
      });
      if (d.ordered_via && !ORDERED_VIA_OPTIONS.includes(d.ordered_via)) {
        setShowCustomVia(true);
      }
      // Auto-show verify payment dialog for delivered but unpaid orders
      if (d.delivery_status === 'delivered' && d.payment_status !== 'paid') {
        setVerifyPaymentDialog(true);
      }
    } catch (e) {
      toast.error('Failed to load order');
      navigate(`/orders/${outletId}`);
    } finally {
      setLoading(false);
    }
  }, [orderId, outletId, getAuthHeader, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveOrder = async (data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${orderId}`, data, { headers: getAuthHeader() });
      toast.success('Order updated', { duration: 500 });
      setTimeout(() => navigate(`/orders/${outletId}`), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => saveOrder(formData);

  // Payment method selection → ask "Paid?"
  const handlePaymentMethodChange = (value) => {
    if (value === 'none') {
      setFormData(prev => ({ ...prev, payment_method: '', payment_status: '' }));
      return;
    }
    setPendingPaymentMethod(value);
    setPaidDialog(true);
  };

  const handlePaidResponse = (paid) => {
    setPaidDialog(false);
    const method = pendingPaymentMethod;
    if (paid) {
      setFormData(prev => ({ ...prev, payment_method: method, payment_status: 'paid' }));
    } else {
      setFormData(prev => ({
        ...prev, payment_method: method,
        payment_status: method === 'cash' ? 'collect' : 'upon_delivery'
      }));
    }
    setPendingPaymentMethod('');
  };

  // Delivery status: no popup for "Delivered" anymore
  const handleDeliveryStatusChange = (value) => {
    if (value === 'none') {
      setFormData(prev => ({ ...prev, delivery_status: '', delivery_reason: '' }));
    } else if (value === 'delivered') {
      if (formData.payment_status === 'paid') {
        // Payment received → auto-complete
        setFormData(prev => ({
          ...prev, delivery_status: 'delivered', status: 'delivered_paid', delivery_reason: ''
        }));
      } else {
        // Payment NOT received → mark delivered, stays active for verification
        setFormData(prev => ({
          ...prev, delivery_status: 'delivered', delivery_reason: ''
        }));
      }
    } else if (value === 'not_delivered') {
      setNotDeliveredDialog(true);
    }
  };

  const handleNotDeliveredReason = (reason) => {
    setNotDeliveredDialog(false);
    const statusMap = { 'Cancelled': 'rejected', 'No Answer': 'rejected', 'Later': 'new' };
    setFormData(prev => ({
      ...prev, delivery_status: 'not_delivered', delivery_reason: reason,
      status: statusMap[reason] || 'new'
    }));
  };

  // Verify Payment flow for delivered+unpaid orders
  const handleVerifyPayment = (verified) => {
    setVerifyPaymentDialog(false);
    if (verified) {
      setFormData(prev => ({
        ...prev, payment_status: 'paid', status: 'delivered_paid'
      }));
      // Auto-save when verified
      saveOrder({ ...formData, payment_status: 'paid', status: 'delivered_paid' });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await axios.delete(`${API}/orders/${orderId}`, { headers: getAuthHeader() });
      toast.success('Order deleted', { duration: 500 });
      setTimeout(() => navigate(`/orders/${outletId}`), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete order');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new': return <Badge className="status-new">New</Badge>;
      case 'delivered_paid': return <Badge className="status-delivered-paid">Delivered & Paid</Badge>;
      case 'delivered_no_payment': return <Badge className="status-delivered-no-payment">Delivered (No Payment)</Badge>;
      case 'rejected': return <Badge className="status-rejected">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPaymentStatusLabel = () => {
    const { payment_method, payment_status } = formData;
    if (!payment_method) return null;
    if (payment_status === 'paid') return { text: `${payment_method === 'cash' ? 'Cash' : 'Transfer'} - Paid`, color: 'text-emerald-500' };
    if (payment_status === 'collect') return { text: 'Cash - Collect', color: 'text-red-500 font-bold' };
    if (payment_status === 'upon_delivery') return { text: 'Transfer - Upon Delivery', color: 'text-red-500 font-bold' };
    return { text: payment_method === 'cash' ? 'Cash' : 'Transfer', color: 'text-muted-foreground' };
  };

  const isDeliveredUnpaid = formData.delivery_status === 'delivered' && formData.payment_status !== 'paid';

  const isOwnerOrAdmin = order && (
    order.user_id === user?.id || outlet?.created_by === user?.id || user?.role === 'super_admin'
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const paymentLabel = getPaymentStatusLabel();

  return (
    <div className="min-h-screen bg-background" data-testid="order-detail-page">
      <Navbar onOpenSettings={() => setSettingsOpen(true)} />
      
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/orders/${outletId}`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold font-mono">#{orderId.slice(0, 8).toUpperCase()}</h1>
                {getStatusBadge(order?.status)}
              </div>
              <p className="text-sm text-muted-foreground">{outlet?.name}</p>
            </div>
          </div>
          {isOwnerOrAdmin && (
            <Button variant="outline" className="text-red-600" onClick={handleDelete} data-testid="delete-order-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          )}
        </div>

        {/* Delivered - Verify Payment banner */}
        {isDeliveredUnpaid && (
          <Card className="mb-6 border-red-500/50 bg-red-500/10">
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <p className="text-red-500 font-bold text-lg">Delivered - Verify Payment</p>
                <p className="text-sm text-muted-foreground">This order has been delivered but payment is not yet verified.</p>
              </div>
              <Button onClick={() => setVerifyPaymentDialog(true)} data-testid="verify-payment-btn">
                Verify Payment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Created: {new Date(order?.created_at).toLocaleString()}</span>
              </div>
              {order?.updated_at !== order?.created_at && (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>Updated: {new Date(order?.updated_at).toLocaleString()}</span>
                </div>
              )}
              {formData.delivery_status === 'delivered' && (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Delivered</Badge>
              )}
              {formData.delivery_status === 'not_delivered' && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                  Not Delivered {formData.delivery_reason ? `- ${formData.delivery_reason}` : ''}
                </Badge>
              )}
              {paymentLabel && <span className={`font-medium ${paymentLabel.color}`}>{paymentLabel.text}</span>}
            </div>
          </CardContent>
        </Card>

        {/* Order Details Form */}
        <Card data-testid="order-form">
          <CardHeader><CardTitle>Order Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {isOwnerOrAdmin && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="delivered_no_payment">Delivered (No Payment)</SelectItem>
                    <SelectItem value="delivered_paid">Delivered & Paid</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Delivery Status</Label>
              <Select value={formData.delivery_status || 'none'} onValueChange={handleDeliveryStatusChange}>
                <SelectTrigger data-testid="delivery-status-select"><SelectValue placeholder="Select delivery status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Select --</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="not_delivered">Not Delivered</SelectItem>
                </SelectContent>
              </Select>
              {formData.delivery_reason && (
                <p className="text-sm text-muted-foreground mt-1">Reason: <span className="font-medium text-foreground">{formData.delivery_reason}</span></p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Location</Label>
                <Input value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} disabled={!isOwnerOrAdmin} data-testid="location-input" />
              </div>
              <div className="space-y-2">
                <Label>Contact Number</Label>
                <div className="flex items-center gap-2">
                  <Input value={formData.contact_no} onChange={(e) => setFormData(prev => ({ ...prev, contact_no: e.target.value }))} disabled={!isOwnerOrAdmin} data-testid="contact-input" />
                  {formData.contact_no && (
                    <a href={`tel:${formData.contact_no}`} className="shrink-0">
                      <Button type="button" variant="outline" size="icon" data-testid="call-btn"><Phone className="h-4 w-4" /></Button>
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Item Details</Label>
                <Input value={formData.item_details} onChange={(e) => setFormData(prev => ({ ...prev, item_details: e.target.value }))} disabled={!isOwnerOrAdmin} data-testid="item-details-input" />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))} disabled={!isOwnerOrAdmin} data-testid="quantity-input" />
              </div>
            </div>

            {isOwnerOrAdmin && (
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={formData.payment_method || 'none'} onValueChange={handlePaymentMethodChange}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Select payment method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select --</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
                {paymentLabel && <p className={`text-sm mt-1 ${paymentLabel.color}`}>{paymentLabel.text}</p>}
              </div>
            )}

            {isOwnerOrAdmin && (
              <div className="space-y-2">
                <Label>Ordered Via</Label>
                <Select
                  value={ORDERED_VIA_OPTIONS.includes(formData.ordered_via) ? formData.ordered_via : (formData.ordered_via ? 'Other' : '')}
                  onValueChange={(v) => {
                    if (v === 'Other') { setShowCustomVia(true); setFormData(prev => ({ ...prev, ordered_via: '' })); }
                    else { setShowCustomVia(false); setFormData(prev => ({ ...prev, ordered_via: v })); }
                  }}
                >
                  <SelectTrigger data-testid="ordered-via-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                  <SelectContent>
                    {ORDERED_VIA_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                {showCustomVia && (
                  <Input value={formData.ordered_via} onChange={(e) => setFormData(prev => ({ ...prev, ordered_via: e.target.value }))} placeholder="Enter custom platform..." className="mt-2" autoFocus data-testid="ordered-via-custom-input" />
                )}
              </div>
            )}

            {isOwnerOrAdmin && (
              <div className="space-y-2">
                <Label>Customer Notes</Label>
                <Textarea value={formData.customer_notes} onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))} placeholder="Any special instructions..." rows={3} data-testid="notes-input" />
              </div>
            )}

            <div className="flex justify-end pt-4">
              <Button onClick={handleSave} disabled={saving} data-testid="save-order-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Save className="h-4 w-4 mr-2" /> Save Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* "Paid?" Dialog */}
      <Dialog open={paidDialog} onOpenChange={setPaidDialog}>
        <DialogContent data-testid="paid-dialog">
          <DialogHeader>
            <DialogTitle>Paid?</DialogTitle>
            <DialogDescription>Has the payment been made via <span className="font-semibold capitalize">{pendingPaymentMethod}</span>?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-0">
            <Button variant="outline" onClick={() => handlePaidResponse(false)} data-testid="paid-no-btn">
              No{pendingPaymentMethod === 'cash' ? ' - Collect' : ' - Upon Delivery'}
            </Button>
            <Button onClick={() => handlePaidResponse(true)} data-testid="paid-yes-btn">Yes - Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Payment Dialog */}
      <Dialog open={verifyPaymentDialog} onOpenChange={setVerifyPaymentDialog}>
        <DialogContent data-testid="verify-payment-dialog">
          <DialogHeader>
            <DialogTitle>Payment Verified?</DialogTitle>
            <DialogDescription>This order has been delivered. Has the payment been verified?</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 sm:gap-0">
            <Button variant="outline" onClick={() => handleVerifyPayment(false)} data-testid="verify-no-btn">Not Verified</Button>
            <Button onClick={() => handleVerifyPayment(true)} data-testid="verify-yes-btn">Verified - Complete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Not Delivered Reason Dialog */}
      <Dialog open={notDeliveredDialog} onOpenChange={setNotDeliveredDialog}>
        <DialogContent data-testid="not-delivered-dialog">
          <DialogHeader>
            <DialogTitle>Reason for Non-Delivery</DialogTitle>
            <DialogDescription>Why was this order not delivered?</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button variant="outline" className="w-full justify-start h-12" onClick={() => handleNotDeliveredReason('No Answer')} data-testid="reason-no-answer-btn">
              <Phone className="h-4 w-4 mr-3 text-amber-500" /> No Answer
            </Button>
            <Button variant="outline" className="w-full justify-start h-12" onClick={() => handleNotDeliveredReason('Later')} data-testid="reason-later-btn">
              <Clock className="h-4 w-4 mr-3 text-blue-500" /> Later
            </Button>
            <Button variant="outline" className="w-full justify-start h-12" onClick={() => handleNotDeliveredReason('Cancelled')} data-testid="reason-cancelled-btn">
              <XCircle className="h-4 w-4 mr-3 text-red-500" /> Cancelled
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
};

export default OrderDetail;
